import { Phone, Mail, MapPin, CreditCard, Shield } from "lucide-react";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { label: "Services", href: "#services" },
    { label: "Service Areas", href: "#areas" },
    { label: "Why Choose Us", href: "#why-us" },
    { label: "Testimonials", href: "#testimonials" },
    { label: "FAQ", href: "#faq" },
    { label: "Contact", href: "#contact" },
  ];

  const serviceAreas = [
    "Tucson",
    "Oro Valley",
    "Catalina",
    "Sahuarita",
    "SaddleBrooke",
    "Oracle",
  ];

  return (
    <footer className="bg-[#1A1A1A] text-white relative overflow-hidden noise-bg" data-testid="footer">
      <div className="relative z-10">
        {/* Main Footer */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            {/* Company Info */}
            <div className="lg:col-span-1">
              <div className="flex items-center gap-2 mb-6">
                <div className="w-10 h-10 bg-[#FF4D00] flex items-center justify-center rounded-sm">
                  <Shield className="w-6 h-6 text-white" strokeWidth={2.5} />
                </div>
                <div>
                  <span className="font-heading font-bold text-white text-lg uppercase tracking-tight block leading-tight">
                    Tucson Stump Grinding
                  </span>
                  <span className="font-body text-xs text-gray-400 block">
                    Tree Stump Removal Service
                  </span>
                </div>
              </div>
              <p className="font-body text-gray-400 text-sm mb-6 leading-relaxed">
                Professional stump grinding and tree stump removal services serving Tucson, Arizona and surrounding communities. Licensed, insured, and committed to quality.
              </p>
              <div className="space-y-3">
                <a
                  href="tel:+15202007739,2"
                  className="flex items-center gap-3 footer-link text-sm"
                >
                  <Phone className="w-4 h-4 text-[#FF4D00]" />
                  (520) 200-7739 Ext 2
                </a>
                <a
                  href="mailto:info@tucsonstumpgrinding.com"
                  className="flex items-center gap-3 footer-link text-sm"
                >
                  <Mail className="w-4 h-4 text-[#FF4D00]" />
                  info@tucsonstumpgrinding.com
                </a>
                <div className="flex items-center gap-3 text-gray-400 text-sm">
                  <MapPin className="w-4 h-4 text-[#FF4D00]" />
                  Tucson, Arizona
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="font-heading font-bold text-white uppercase text-sm mb-6 tracking-wider">
                Quick Links
              </h3>
              <ul className="space-y-3">
                {quickLinks.map((link) => (
                  <li key={link.href}>
                    <a
                      href={link.href}
                      className="footer-link text-sm font-body"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Service Areas */}
            <div>
              <h3 className="font-heading font-bold text-white uppercase text-sm mb-6 tracking-wider">
                Service Areas
              </h3>
              <ul className="space-y-3">
                {serviceAreas.map((area) => (
                  <li key={area}>
                    <span className="footer-link text-sm font-body cursor-default">
                      {area}, AZ
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Services & Payment */}
            <div>
              <h3 className="font-heading font-bold text-white uppercase text-sm mb-6 tracking-wider">
                Our Services
              </h3>
              <ul className="space-y-3">
                <li>
                  <span className="text-gray-400 text-sm font-body">Stump Grinding</span>
                </li>
                <li>
                  <span className="text-gray-400 text-sm font-body">Tree Stump Removal</span>
                </li>
                <li>
                  <span className="text-gray-400 text-sm font-body">Root Grinding</span>
                </li>
                <li>
                  <span className="text-gray-400 text-sm font-body">Land Clearing</span>
                </li>
                <li>
                  <a href="/tree-removal" className="text-green-500 text-sm font-body hover:text-green-400 transition-colors">
                    🌳 Tree Removal
                  </a>
                </li>
              </ul>

              {/* CTA */}
              <div className="mt-6">
                <a
                  href="#quote"
                  className="btn-primary px-6 py-3 inline-block text-sm"
                  data-testid="footer-quote-btn"
                >
                  Get Free Quote
                </a>
              </div>

              {/* Payment Methods */}
              <div className="mt-6">
                <h4 className="font-heading font-bold text-white uppercase text-xs mb-3 tracking-wider flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-[#FF4D00]" />
                  We Accept
                </h4>
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Visa */}
                  <div className="bg-white rounded px-2 py-1" title="Visa">
                    <svg viewBox="0 0 48 32" className="h-6 w-auto">
                      <rect fill="#1434CB" width="48" height="32" rx="4"/>
                      <path fill="#fff" d="M19.5 21.5h-3l1.9-11.5h3l-1.9 11.5zm7.7-11.2c-.6-.2-1.5-.5-2.7-.5-3 0-5.1 1.5-5.1 3.7 0 1.6 1.5 2.5 2.6 3 1.2.6 1.6 1 1.6 1.5 0 .8-1 1.2-1.9 1.2-1.2 0-1.9-.2-2.9-.6l-.4-.2-.4 2.5c.7.3 2.1.6 3.5.6 3.2 0 5.2-1.5 5.3-3.8 0-1.3-.8-2.3-2.5-3.1-1-.5-1.7-.9-1.7-1.4 0-.5.5-1 1.7-1 1 0 1.7.2 2.2.4l.3.1.4-2.4zm7.9-.3h-2.3c-.7 0-1.3.2-1.6.9l-4.4 10.6h3.2l.6-1.7h3.9l.4 1.7h2.8l-2.6-11.5zm-3.7 7.4l1.2-3.3c0 0 .3-.7.4-1.1l.2 1 .7 3.4h-2.5zm-15.9-7.4l-2.8 7.8-.3-1.5c-.5-1.7-2.1-3.6-3.9-4.5l2.7 10.2h3.2l4.8-12h-3.7z"/>
                      <path fill="#F9A533" d="M9.9 10h-5l-.1.3c3.8.9 6.3 3.2 7.4 5.9l-1.1-5.3c-.2-.7-.7-.9-1.2-.9z"/>
                    </svg>
                  </div>
                  {/* Mastercard */}
                  <div className="bg-white rounded px-2 py-1" title="Mastercard">
                    <svg viewBox="0 0 48 32" className="h-6 w-auto">
                      <rect fill="#000" width="48" height="32" rx="4"/>
                      <circle fill="#EB001B" cx="18" cy="16" r="9"/>
                      <circle fill="#F79E1B" cx="30" cy="16" r="9"/>
                      <path fill="#FF5F00" d="M24 9.3a9 9 0 0 0-3.4 6.7 9 9 0 0 0 3.4 6.7 9 9 0 0 0 3.4-6.7 9 9 0 0 0-3.4-6.7z"/>
                    </svg>
                  </div>
                  {/* American Express */}
                  <div className="bg-[#016FD0] rounded px-2 py-1" title="American Express">
                    <svg viewBox="0 0 48 32" className="h-6 w-auto">
                      <rect fill="#016FD0" width="48" height="32" rx="4"/>
                      <path fill="#fff" d="M7 22.5v-6h4.5l.5 1 .6-1h31v5.5s-.5.5-1.1.5H27l-.6-1.2v1.2h-3.5v-2s-.3.2-1 .2h-.6v1.8h-5.5l-.5-1-.5 1H7zm1-5v4h2.5l.5-1 .5 1h1.5l-1.2-2 1.2-2h-1.5l-.5 1-.5-1H8zm8.5 0v4h1v-1.5h1l.8 1.5h1.2l-.9-1.6c.4-.2.7-.6.7-1.2 0-.8-.5-1.2-1.3-1.2h-2.5zm1 1h1.3c.3 0 .4.1.4.3s-.1.4-.4.4h-1.3v-.7zm3.5-1v4h1v-4h-1zm2 0v4h1v-1.5h.6c.9 0 1.4-.5 1.4-1.3 0-.7-.5-1.2-1.4-1.2H21zm1 1h.6c.3 0 .4.1.4.3 0 .2-.1.3-.4.3h-.6v-.6zm3 3h3v-1h-2v-.5h2v-1h-2V18h2v-1h-3v4zm4-4v4h1v-1.2l.2-.2.9 1.4h1.3l-1.4-2 1.3-1.6h-1.3l-1 1.3V18h-1zm4.5 0l-1.5 4h1.1l.3-.7h1.5l.3.7h1.1l-1.4-4H34zm.5 1.1l.5 1.2h-1l.5-1.2z"/>
                    </svg>
                  </div>
                  {/* Discover */}
                  <div className="bg-white rounded px-2 py-1" title="Discover">
                    <svg viewBox="0 0 48 32" className="h-6 w-auto">
                      <rect fill="#fff" width="48" height="32" rx="4"/>
                      <rect fill="#F47216" x="0" y="0" width="48" height="32" rx="4"/>
                      <ellipse fill="#fff" cx="30" cy="16" rx="8" ry="7"/>
                      <path fill="#F47216" d="M28 11h4v10h-4z"/>
                      <text x="6" y="19" fill="#fff" fontSize="8" fontFamily="Arial" fontWeight="bold">DISCOVER</text>
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-[#FF4D00] flex items-center justify-center rounded-sm">
                  <Shield className="w-5 h-5 text-white" strokeWidth={2.5} />
                </div>
                <div>
                  <p className="font-body text-gray-500 text-sm">
                    &copy; {currentYear} Tucson Stump Grinding - Tree Stump Removal Service. All rights reserved.
                  </p>
                </div>
              </div>
              <p className="font-body text-gray-500 text-sm text-center md:text-right">
                Proudly serving Tucson, Arizona since 2014
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
